'use client';

import { COVEO_URL_CHANGE_EVENT } from '@lib/coveo/urlChangeEvent';
import {
  SearchResultsBannerDisplayType,
  SearchResultsBannerDisplayTypePriority,
  SearchResultsBanner as SearchResultsBannerType,
} from '@lib/types/search/SearchResultsBanner';
import c from 'classnames';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { Link } from 'ui-components';

type SearchResultsBannerProps = {
  banners: SearchResultsBannerType[];
};

const isBannerDisplayable = (
  banner: SearchResultsBannerType,
  filtersString: string | null
) => {
  if (banner.displayType === SearchResultsBannerDisplayType.FLASH_DEAL) {
    return filtersString?.includes('flash_deal');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.NEW_RELEASE) {
    return filtersString?.includes('new_release_discount');
  }

  if (banner.displayType === SearchResultsBannerDisplayType.ON_SALE) {
    return filtersString?.includes('on_sale');
  }

  return banner.displayType === SearchResultsBannerDisplayType.DEFAULT;
};

const getActiveBanner = (
  banners: SearchResultsBannerType[],
  filtersString: string | null
) =>
  banners
    .filter((banner) => isBannerDisplayable(banner, filtersString))
    .sort(
      (a, b) =>
        SearchResultsBannerDisplayTypePriority[a.displayType] -
        SearchResultsBannerDisplayTypePriority[b.displayType]
    )[0];

/**
 * Tracks the current URL hash fragment. Updates on browser navigation
 * (`popstate`, `hashchange`) and on in-app history writes broadcast by the
 * Coveo URL managers via {@link COVEO_URL_CHANGE_EVENT} (which native
 * `pushState`/`replaceState` don't emit themselves).
 */
const useUrlHashFragment = (): string => {
  const [fragment, setFragment] = useState('');
  useEffect(() => {
    const read = () => setFragment(window.location.hash.slice(1));
    read();
    window.addEventListener('popstate', read);
    window.addEventListener('hashchange', read);
    window.addEventListener(COVEO_URL_CHANGE_EVENT, read);
    return () => {
      window.removeEventListener('popstate', read);
      window.removeEventListener('hashchange', read);
      window.removeEventListener(COVEO_URL_CHANGE_EVENT, read);
    };
  }, []);
  return fragment;
};

export const SearchResultsBanner = ({ banners }: SearchResultsBannerProps) => {
  const fragment = useUrlHashFragment();
  const saleFilter = new URLSearchParams(fragment).get('f-ec_sale_filters');
  const activeBanner = getActiveBanner(banners, saleFilter);

  if (!activeBanner) {
    return null;
  }

  return (
    <Link
      href={activeBanner.link.href}
      target={activeBanner.link.openInNewTab ? '_blank' : undefined}
      className={c('relative mb-6 block w-full md:aspect-[32/5] lg:mb-14', {
        ['aspect-[32/5]']: !activeBanner.mobileImage,
        ['aspect-[49/16]']: activeBanner.mobileImage,
      })}
    >
      <Image
        fill
        sizes="100vw"
        src={activeBanner.image.url}
        alt={activeBanner.image.alt ?? ''}
        className={c('object-cover', {
          ['hidden md:block']: activeBanner.mobileImage,
        })}
      />
      {activeBanner.mobileImage && (
        <Image
          fill
          sizes="100vw, (min-width: 768px) 0vw"
          src={activeBanner.mobileImage.url}
          alt={activeBanner.mobileImage.alt ?? ''}
          className={c('block object-cover md:hidden')}
        />
      )}
    </Link>
  );
};
